/**
 * @description Timezone-safe date utilities for the customer portal.
 *
 * Mirrors the established merchant-frontend pattern from
 * frontend/src/utils/invoiceHelpers.js (parseDateSafely + formatInvoiceDate).
 * Customer portal previously had this logic re-implemented inline in each
 * component using the buggy `new Date(str).toLocaleDateString()` pattern,
 * which UTC-shifts YYYY-MM-DD strings and renders them as the previous day
 * in negative-offset timezones.
 *
 * Use these helpers anywhere a backend DATE column (invoice_date, due_date,
 * payment_date, expiration_date, etc.) is rendered or compared.
 * Invoice overdue prefers API displayStatus (company-local SQL). Client
 * fallback uses the same YYYY-MM-DD comparison, never laptop-local Date.
 */

/** @type {string} */
export const DEFAULT_COMPANY_TIMEZONE = 'America/New_York';

/**
 * Resolve an IANA timezone; invalid names fall back to America/New_York.
 * @param {string} [timeZone]
 * @returns {string}
 */
export const resolveIanaTimeZone = (timeZone) => {
  if (typeof timeZone !== 'string' || !timeZone.trim()) {
    return DEFAULT_COMPANY_TIMEZONE;
  }
  const candidate = timeZone.trim();
  try {
    Intl.DateTimeFormat('en-US', { timeZone: candidate });
    return candidate;
  } catch {
    return DEFAULT_COMPANY_TIMEZONE;
  }
};

/**
 * @param {string|Date|null|undefined} dateValue
 * @returns {string|null} YYYY-MM-DD
 */
export const toCalendarDateString = (dateValue) => {
  if (!dateValue) return null;
  if (typeof dateValue === 'string') {
    const match = dateValue.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : null;
  }
  if (dateValue instanceof Date && !Number.isNaN(dateValue.getTime())) {
    const year = dateValue.getFullYear();
    const month = String(dateValue.getMonth() + 1).padStart(2, '0');
    const day = String(dateValue.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  return null;
};

/**
 * @param {string} [timeZone]
 * @param {Date} [now]
 * @returns {string} YYYY-MM-DD
 */
export const getTodayDateStringInTimeZone = (timeZone, now = new Date()) => {
  const tz = resolveIanaTimeZone(timeZone);
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(now);
  const year = parts.find((part) => part.type === 'year')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;
  const day = parts.find((part) => part.type === 'day')?.value;
  return year && month && day ? `${year}-${month}-${day}` : null;
};

/**
 * @param {object} invoice
 * @param {string} [timeZone]
 * @returns {boolean}
 */
export const isPortalInvoiceOverdue = (invoice, timeZone) => {
  const paymentStatus = invoice?.paymentStatus;
  if (paymentStatus === 'paid' || paymentStatus === 'voided') return false;
  if (typeof invoice?.displayStatus === 'string') {
    return invoice.displayStatus === 'overdue';
  }
  if (paymentStatus !== 'unpaid' && paymentStatus !== 'partially_paid') return false;
  const dueStr = toCalendarDateString(invoice?.dueDate);
  if (!dueStr) return false;
  const todayStr = getTodayDateStringInTimeZone(timeZone);
  return Boolean(todayStr && dueStr < todayStr);
};

/**
 * Parse a date string safely without timezone shifts.
 * For YYYY-MM-DD format, constructs the Date in local time so that
 * toLocaleDateString renders the intended day in any timezone.
 *
 * @param {string} dateString - YYYY-MM-DD or ISO timestamp
 * @returns {Date|null}
 */
export const parseDateSafely = (dateString) => {
  if (!dateString) return null;
  // Slice to 10 chars to handle ISO timestamps like "2026-04-28T00:00:00.000Z"
  const [year, month, day] = dateString.slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day); // month is 0-indexed
};

/**
 * Format a date for display, timezone-safe.
 * Mirrors formatInvoiceDate from frontend/src/utils/invoiceHelpers.js.
 *
 * @param {string|Date} date
 * @param {Intl.DateTimeFormatOptions} [options]
 * @returns {string}
 */
export const formatDate = (
  date,
  options = { year: 'numeric', month: 'long', day: 'numeric' }
) => {
  if (!date) return '';
  const d = typeof date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(date)
    ? parseDateSafely(date)
    : new Date(date);
  return d.toLocaleDateString('en-US', options);
};
